import { Link } from 'react-router-dom';

export interface CTABandProps {
  text: string;
  buttonText: string;
  buttonLink: string;
  showPhone?: boolean;
  variant?: 'gold' | 'dark';
}

export default function CTABand({
  text,
  buttonText,
  buttonLink,
  showPhone = false,
  variant = 'gold',
}: CTABandProps) {
  const isGold = variant === 'gold';

  return (
    <section
      className={`relative py-9 overflow-hidden ${
        isGold ? 'bg-gold' : 'bg-charcoal'
      }`}
    >
      {/* Subtle pattern overlay */}
      <div
        className="absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage: isGold
            ? 'repeating-linear-gradient(90deg, transparent, transparent 79px, rgba(20,20,20,0.6) 79px, rgba(20,20,20,0.6) 80px)'
            : 'repeating-linear-gradient(90deg, transparent, transparent 79px, rgba(201,168,76,0.5) 79px, rgba(201,168,76,0.5) 80px)',
        }}
      />
      {/* Left accent strip */}
      <div
        className={`absolute left-0 top-0 bottom-0 w-1 ${
          isGold ? 'bg-charcoal/20' : 'bg-gold/50'
        }`}
      />

      <div className="relative z-10 mx-auto px-6 lg:px-12 xl:px-20">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-5">
          {/* Text */}
          <div className="flex items-center gap-5">
            <div
              className={`hidden lg:block w-6 h-px flex-shrink-0 ${
                isGold ? 'bg-charcoal/40' : 'bg-gold'
              }`}
            />
            <p
              className={`font-serif text-lg lg:text-xl font-light leading-snug text-center sm:text-left ${
                isGold ? 'text-charcoal' : 'text-white'
              }`}
            >
              {text}
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-5 flex-shrink-0">
            {showPhone && (
              <a
                href="tel:7279149002"
                className={`hidden md:flex items-center gap-2 text-sm font-semibold whitespace-nowrap cursor-pointer transition-opacity duration-300 hover:opacity-70 ${
                  isGold ? 'text-charcoal' : 'text-gold'
                }`}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-phone-line text-sm"></i>
                </div>
                727-914-9002
              </a>
            )}
            <Link
              to={buttonLink}
              className={`whitespace-nowrap inline-flex items-center gap-2.5 text-[10px] font-bold tracking-[0.25em] uppercase px-9 py-3.5 transition-all duration-300 cursor-pointer group ${
                isGold
                  ? 'bg-charcoal text-white hover:bg-charcoal/85'
                  : 'bg-gold text-charcoal hover:bg-gold-light'
              }`}
            >
              {buttonText}
              <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1"></i>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
